/* sugar:assets/email-popup.js@2.2.0 */
/**
 * Drives every popup on the page: the box/circle sections and the full screen
 * one. Each root element carries its own config, so several can coexist
 * without sharing state.
 *
 * Which pages a popup appears on is decided in Liquid before this file runs —
 * a popup that does not belong here was never rendered.
 */
(function () {
  'use strict';

  var DESKTOP_MIN = 750;
  var CONFETTI_COLORS = ['#FFC700', '#FF3D68', '#4CC9F0', '#8AC926', '#FF7B00', '#B5179E'];

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function track(type, sectionId, value, label) {
    if (isDesignMode()) return;
    var ctx = { path: window.location.pathname, label: label || '' };
    if (window.SugarAnalytics) {
      window.SugarAnalytics.track(type, 'popup', sectionId, value, ctx);
    } else {
      (window._sugarQ = window._sugarQ || []).push([type, 'popup', sectionId, value, ctx]);
    }
  }

  function readConfig(popup) {
    var el = popup.querySelector('[data-email-popup-config]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function bool(value) {
    return value === 'true';
  }

  function int(value, fallback) {
    var n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  /* --------------------------------------------- teaser above sticky bars */

  // A teaser along the bottom of the screen moves up while one of the theme's own bottom
  // sticky bars is showing beneath it. This only runs while such a teaser is on screen, and
  // it listens to the bars rather than to scrolling: a bar shows, hides and docks by changing
  // its own classes, so a class change or a size change is the only time the answer moves.
  var STICKY_BARS =
    '.custom-sticky, .sugar-sticky-product-mobile, .sugar-sticky-product-desktop, ' +
    '.sugar-sticky-buy-button, .sugar-sticky-navigation';
  var phone = window.matchMedia('(max-width: ' + (DESKTOP_MIN - 1) + 'px)');
  var shownTeasers = [];
  var stickyBars = [];
  var watchingBars = false;
  var clearanceFrame = 0;
  var barChanges = new MutationObserver(scheduleClearance);
  var barSizes = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleClearance) : null;

  function teaserAtBottom(teaser) {
    var position = phone.matches ? teaser.dataset.mobilePosition : teaser.dataset.position;
    return !!position && position.indexOf('bottom') === 0;
  }

  // Mirrors the bars' own CSS: a bottom bar is on screen once it is revealed, or always when
  // it is docked with no reveal rule, and never while it rests in place or hides at the footer.
  function barShowing(bar) {
    if (bar.dataset.position !== 'bottom') return false;
    if (bar.classList.contains('is-docked') || bar.classList.contains('is-hidden-at-bottom')) return false;
    if (bar.classList.contains('is-visible')) return true;
    if (bar.dataset.docked !== 'true') return false;
    var gated = bar.classList.contains('custom-sticky') &&
      ['custom', 'until', 'viewport'].indexOf(bar.dataset.visibility) !== -1;
    return !gated;
  }

  function watchBars() {
    if (watchingBars) return;
    watchingBars = true;
    stickyBars = Array.prototype.slice.call(document.querySelectorAll(STICKY_BARS));
    stickyBars.forEach(function (bar) {
      barChanges.observe(bar, { attributes: true, attributeFilter: ['class'] });
      if (barSizes) barSizes.observe(bar);
    });
    window.addEventListener('resize', scheduleClearance);
  }

  function unwatchBars() {
    if (!watchingBars) return;
    watchingBars = false;
    stickyBars = [];
    barChanges.disconnect();
    if (barSizes) barSizes.disconnect();
    window.removeEventListener('resize', scheduleClearance);
  }

  function scheduleClearance() {
    if (!clearanceFrame) clearanceFrame = requestAnimationFrame(updateClearance);
  }

  function updateClearance() {
    clearanceFrame = 0;
    shownTeasers = shownTeasers.filter(function (teaser) { return teaser.isConnected; });
    var active = shownTeasers.filter(teaserAtBottom);
    if (!active.length) {
      unwatchBars();
      return;
    }
    watchBars();

    // Every bar and teaser is measured before any teaser is moved.
    var obstacles = [];
    stickyBars.forEach(function (bar) {
      if (!barShowing(bar) || !bar.offsetHeight) return;
      var rect = bar.getBoundingClientRect();
      // The height at rest, not the sliding position, so a bar still on its way in is
      // cleared for where it is going.
      var rise = bar.offsetHeight + (parseFloat(getComputedStyle(bar).bottom) || 0);
      obstacles.push({ left: rect.left, right: rect.right, rise: Math.ceil(rise) });
    });
    var moves = active.map(function (teaser) {
      var rect = teaser.getBoundingClientRect();
      var clearance = 0;
      if (rect.width) {
        obstacles.forEach(function (bar) {
          if (bar.left < rect.right && bar.right > rect.left) clearance = Math.max(clearance, bar.rise);
        });
      }
      return { teaser: teaser, value: clearance + 'px' };
    });

    moves.forEach(function (move) {
      if (move.teaser.style.getPropertyValue('--ept-clearance') !== move.value) {
        move.teaser.style.setProperty('--ept-clearance', move.value);
      }
    });
  }

  function teaserShown(teaser) {
    if (shownTeasers.indexOf(teaser) === -1) shownTeasers.push(teaser);
    scheduleClearance();
  }

  function teaserHidden(teaser) {
    var index = shownTeasers.indexOf(teaser);
    if (index === -1) return;
    shownTeasers.splice(index, 1);
    scheduleClearance();
  }

  // The mobile position can differ from the desktop one, and the editor can add or remove a
  // bar; either way the bars are found again on the next pass.
  if (phone.addEventListener) phone.addEventListener('change', scheduleClearance);
  ['shopify:section:load', 'shopify:section:unload'].forEach(function (name) {
    document.addEventListener(name, function () {
      if (!shownTeasers.length) return;
      unwatchBars();
      scheduleClearance();
    });
  });

  function setup(popup) {
    if (popup.dataset.epReady === 'true') return;
    popup.dataset.epReady = 'true';

    var config = readConfig(popup);
    var sectionId = config.id || popup.dataset.sectionId || '';
    var storageKey = 'email-popup-' + sectionId;
    var testMode = config.testMode || 'off';
    // "teaser" previews the closed state: the popup stays shut, the teaser shows, and a click
    // on it opens the popup the way it would for a visitor. It is not a test mode of the popup
    // itself, so closing the popup still works.
    var teaserPreview = isDesignMode() && testMode === 'teaser';
    var inTestMode = isDesignMode() && testMode !== 'off' && !teaserPreview;
    var closeOnOutside = config.closeOnOutside !== false;

    var frame = popup.querySelector('.sugar-email-popup__frame');
    var closeBtn = popup.querySelector('[data-email-popup-close]');
    var form = popup.querySelector('[data-email-popup-form]');
    var content = popup.querySelector('.sugar-email-popup__content');
    var imageBox = popup.querySelector('.sugar-email-popup__image');

    var preOptInHost = popup.querySelector('[data-email-popup-page="pre-opt-in"]');
    var optInHost = popup.querySelector('[data-email-popup-page="opt-in"]');
    var successHost = popup.querySelector('[data-email-popup-page="success"]');

    var preOptInPage = popup.querySelector('[data-page="pre-opt-in"]');
    var optInPage = popup.querySelector('[data-page="opt-in"]');
    var successPage = popup.querySelector('[data-page="success"]');

    // The teaser is rendered beside the popup, not inside it, so the popup's own hidden
    // state never reaches it. It carries the section id to find its way back.
    var teaser = document.querySelector('[data-email-teaser="' + sectionId + '"]');
    var teaserMode = teaser ? teaser.dataset.mode || 'off' : 'off';
    var hasTeaser = !!teaser && teaserMode !== 'off';
    var teaserOpenBtn = teaser ? teaser.querySelector('[data-email-teaser-open]') : null;
    var teaserCloseBtn = teaser ? teaser.querySelector('[data-email-teaser-close]') : null;

    var hasPreOptIn = !!(preOptInHost && preOptInHost.children.length > 0);

    var d = optInHost ? optInHost.dataset : {};
    var triggerDelay = bool(d.triggerDelay);
    var delayMs = int(d.delaySeconds, 5) * 1000;
    var triggerExit = bool(d.triggerExit);
    var triggerScroll = bool(d.triggerScroll);
    var scrollPercent = int(d.scrollPercent, 50);
    var triggerPages = bool(d.triggerPages);
    var pageCount = int(d.pageCount, 2);
    var hideAfterSubmit = bool(d.hideAfterSubmit);
    var reshowAfter = d.reshowAfter || '3';

    var s = successHost ? successHost.dataset : {};
    var autoClose = bool(s.autoClose);
    var autoCloseMs = int(s.autoCloseDelay, 5) * 1000;
    var redirectUrl = s.redirectUrl || '';
    var showConfetti = bool(s.showConfetti);

    var popupShown = false;
    var hasSubmitted = false;

    /* ------------------------------------------------------------ storage */

    function getState() {
      try {
        var stored = localStorage.getItem(storageKey);
        return stored ? JSON.parse(stored) || {} : {};
      } catch (e) {
        return {};
      }
    }

    function setState(data) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch (e) {
        /* Storage is unavailable in strict privacy modes. */
      }
    }

    function label() {
      var heading = popup.querySelector(
        '.sugar-email-popup__content h1, .sugar-email-popup__content h2, .sugar-email-popup__content h3, .sugar-email-popup__content .h1, .sugar-email-popup__content .h2'
      );
      if (!heading || !heading.textContent) return '';
      return heading.textContent.replace(/\s+/g, ' ').trim().slice(0, 80);
    }

    function emit(type, value) {
      track(type, sectionId, value, label());
    }

    /* -------------------------------------------------- anchor image size */

    function updateAnchorImage() {
      if (!imageBox || !content) return;
      if (imageBox.dataset.imageFit !== 'anchor') return;

      if (window.innerWidth < DESKTOP_MIN) {
        imageBox.style.width = '';
        imageBox.style.height = '';
        return;
      }

      var minWidth = parseFloat(imageBox.dataset.anchorMinWidth) || 300;
      var ratio = parseFloat(imageBox.dataset.imageAspectRatio) || 1;
      var width = Math.max(minWidth, content.offsetHeight * ratio);

      imageBox.style.width = width + 'px';
      imageBox.style.height = width / ratio + 'px';
    }

    if (imageBox && imageBox.dataset.imageFit === 'anchor') {
      setTimeout(updateAnchorImage, 50);
      window.addEventListener('load', updateAnchorImage);
      window.addEventListener('resize', updateAnchorImage);

      new MutationObserver(function () {
        if (popup.classList.contains('is-open')) setTimeout(updateAnchorImage, 50);
      }).observe(popup, { attributes: true, attributeFilter: ['class'] });

      if (isDesignMode()) {
        document.addEventListener('shopify:section:load', updateAnchorImage);
      }
    }

    /* ------------------------------------------------------------ images */

    // A popup starts hidden, and a lazy image inside something hidden is never in view, so the
    // browser has nothing to trigger the fetch with — the picture is still blank when the popup
    // finally opens. Warming them after the page has loaded keeps them off the critical path
    // while making sure they are there before any trigger can fire.
    function warmImages() {
      popup.querySelectorAll('img[loading="lazy" i]').forEach(function (img) {
        img.loading = 'eager';
      });
    }

    if (document.readyState === 'complete') {
      warmImages();
    } else {
      window.addEventListener('load', warmImages, { once: true });
    }

    /* ------------------------------------------------------- should show? */

    // The "show again" rule, applied to whichever close is being asked about: the popup's
    // own, or the teaser's.
    function reshowAllows(closedAt, sessionKey) {
      if (!closedAt) return true;
      if (reshowAfter === 'never') return false;
      if (reshowAfter === 'session') return !sessionStorage.getItem(sessionKey);

      var days = parseInt(reshowAfter, 10);
      if (!Number.isFinite(days)) return true;

      var elapsed = (Date.now() - new Date(closedAt).getTime()) / 86400000;
      return elapsed >= days;
    }

    function shouldShow() {
      if (inTestMode || isDesignMode()) return false;
      if (window.location.pathname === '/challenge') return false;

      var data = getState();
      if (data.submitted && hideAfterSubmit) return false;
      return reshowAllows(data.closedAt, storageKey + '-closed-session');
    }

    /* ------------------------------------------------------------- teaser */

    // The teaser has its own memory. Closing the popup never hides it — that is what it is
    // for — and closing the teaser never blocks the popup's own triggers. In the editor it
    // only appears in its own preview mode, so it never sits over something being designed.
    function teaserAllowed() {
      if (!hasTeaser) return false;
      if (isDesignMode()) return teaserPreview;
      if (window.location.pathname === '/challenge') return false;

      var data = getState();
      if (data.submitted && hideAfterSubmit) return false;
      if (teaserMode === 'after' && !data.closedAt) return false;
      return reshowAllows(data.teaserClosedAt, storageKey + '-teaser-session');
    }

    function showTeaser() {
      if (!teaser || popup.classList.contains('is-open')) return;
      if (!teaserAllowed()) return;
      teaser.classList.add('is-visible');
      teaserShown(teaser);
    }

    function hideTeaser() {
      if (!teaser) return;
      teaser.classList.remove('is-visible');
      teaserHidden(teaser);
    }

    // A corner ribbon is sized from its words: the triangle is as small as the text allows,
    // and the text sits with the same gap to the long edge as to the corner. Everything is
    // in em so the mobile font size needs no second pass.
    function sizeRibbon() {
      if (!teaser || teaser.dataset.style !== 'ribbon') return;
      var body = teaser.querySelector('.sugar-email-teaser__body');
      var fs = parseFloat(getComputedStyle(teaser).fontSize) || 0;
      if (!body || !fs || !body.offsetWidth) return;

      var w = body.offsetWidth / fs;
      var h = body.offsetHeight / fs;
      var gap = 0.7;
      var reach = w / 2 + h + gap * 2;
      teaser.style.setProperty('--ept-ribbon', (reach * Math.SQRT2).toFixed(2) + 'em');
      // The nudge is applied on both axes, so the perpendicular distance is divided by √2.
      teaser.style.setProperty('--ept-in', ((gap + h / 2) / Math.SQRT2).toFixed(2) + 'em');
    }

    // A circle grows to stay round when its words stack taller than they are wide, but the
    // wrapper that anchors the close disc keeps the narrower width and the circle spills past
    // it. The wrapper is told the circle's real width so the disc sits off the curve.
    function sizeCircle() {
      if (!teaser || teaser.dataset.style !== 'circle') return;
      var open = teaser.querySelector('.sugar-email-teaser__open');
      if (!open) return;
      teaser.style.width = '';
      var w = open.getBoundingClientRect().width;
      if (w) teaser.style.width = w + 'px';
    }

    function sizeTeaser() {
      sizeRibbon();
      sizeCircle();
    }

    if (teaser) {
      sizeTeaser();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(sizeTeaser);
      // The mobile font size lands at the breakpoint, so measure again when it flips.
      var bp = window.matchMedia('(max-width: 749px)');
      if (bp.addEventListener) bp.addEventListener('change', sizeTeaser);
    }

    function closeTeaser() {
      hideTeaser();

      // In its preview mode the teaser comes back after a moment, so the close can be tried
      // without a reload to get it back.
      if (isDesignMode()) {
        if (teaserPreview) setTimeout(showTeaser, 1500);
        return;
      }

      var data = getState();
      data.teaserClosedAt = new Date().toISOString();
      setState(data);
      sessionStorage.setItem(storageKey + '-teaser-session', 'true');
      emit('dismiss', 'teaser');
    }

    /* ---------------------------------------------------------- confetti */

    function launchConfetti() {
      if (!showConfetti) return;

      var existing = popup.querySelector('.sugar-email-popup__confetti');
      if (existing) existing.remove();

      var root = document.createElement('div');
      root.className = 'sugar-email-popup__confetti';

      for (var i = 0; i < 60; i++) {
        var piece = document.createElement('span');
        piece.className = 'sugar-email-popup__confetti-piece';
        piece.style.setProperty('--confetti-left', Math.random() * 100 + '%');
        piece.style.setProperty('--confetti-delay', Math.random() * 0.3 + 's');
        piece.style.setProperty('--confetti-duration', 1.2 + Math.random() * 1.1 + 's');
        piece.style.setProperty('--confetti-drift', Math.round((Math.random() - 0.5) * 120) + 'px');
        piece.style.setProperty('--confetti-color', CONFETTI_COLORS[i % CONFETTI_COLORS.length]);
        root.appendChild(piece);
      }

      popup.appendChild(root);
      setTimeout(function () {
        root.remove();
      }, 2400);
    }

    /* ------------------------------------------------------- page switching */

    function activate(page) {
      [preOptInPage, optInPage, successPage].forEach(function (el) {
        if (el) el.classList.toggle('is-active', el === page);
      });
    }

    // Show on desktop / Show on mobile off hides the popup with its stylesheet on that side of
    // 750px. Opened there anyway, it would lock the page's scrolling behind a dialog nobody can
    // see or close, so it stays shut (and closes if the window crosses into that side).
    var phoneQuery = window.matchMedia('(max-width: 749px)');
    function hiddenHere() {
      return popup.hasAttribute(phoneQuery.matches ? 'data-hide-mobile' : 'data-hide-desktop');
    }
    phoneQuery.addEventListener('change', function () {
      if (popup.classList.contains('is-open') && hiddenHere()) close();
    });

    // A click on the teaser is the visitor asking, so it opens the popup even when the
    // "show again" rule would hold an automatic trigger back.
    function open(trigger, force) {
      if (popupShown || hiddenHere()) return;
      if (!force && !shouldShow()) return;
      popupShown = true;
      hideTeaser();
      popup.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      emit('impression', trigger || '');
      activate(hasPreOptIn ? preOptInPage : optInPage);
    }

    function close() {
      if (inTestMode) return;

      popup.classList.remove('is-open');
      document.body.style.overflow = '';

      // Opened from the teaser inside the editor: nothing to remember.
      if (isDesignMode()) {
        showTeaser();
        return;
      }

      var active = popup.querySelector('.sugar-email-popup__page.is-active');
      emit('dismiss', active ? active.dataset.page || '' : '');

      // The teaser may bring it back, so the guard that stops a second automatic
      // opening has to let go here.
      popupShown = false;

      if (!hasSubmitted) {
        var data = getState();
        data.closedAt = new Date().toISOString();
        setState(data);
        sessionStorage.setItem(storageKey + '-closed-session', 'true');
      }

      showTeaser();
    }

    function showOptIn() {
      activate(optInPage);
      emit('step', 'opt-in');
    }

    function showSuccess() {
      activate(successPage);
      emit('step', 'success');
      launchConfetti();
      hasSubmitted = true;

      var data = getState();
      data.submitted = true;
      data.submittedAt = new Date().toISOString();
      setState(data);

      if (!autoClose) return;
      setTimeout(function () {
        close();
        if (redirectUrl) window.location.href = redirectUrl;
      }, autoCloseMs);
    }

    /* --------------------------------------------------------- editor mode */

    function showTestPage(preferred) {
      hideTeaser();
      popup.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      var target = preferred || config.testPage || 'opt-in';
      if (target === 'pre-opt-in' && !hasPreOptIn) target = 'opt-in';

      activate(popup.querySelector('[data-page="' + target + '"]') || optInPage);

      // The success page only ever fired confetti on a real submission, so it
      // was invisible to anyone building the page. Preview it here too.
      if (target === 'success') launchConfetti();
    }

    function editorSelectedPage(event) {
      if (!event || !event.detail || event.detail.sectionId !== sectionId) return null;

      var selected = event.detail.blockId
        ? document.getElementById('shopify-block-' + event.detail.blockId)
        : null;

      if (!selected && event.target instanceof Element) {
        selected = event.target.closest('[id^="shopify-block-"]');
      }
      if (!selected || !popup.contains(selected)) return null;

      var host = selected.closest('[data-page]');
      return host ? host.getAttribute('data-page') : null;
    }

    /* ----------------------------------------------------------- listeners */

    if (closeBtn) closeBtn.addEventListener('click', close);

    if (teaserOpenBtn) {
      teaserOpenBtn.addEventListener('click', function () {
        if (isDesignMode()) showTestPage();
        else open('teaser', true);
      });
    }

    if (teaserCloseBtn) {
      teaserCloseBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        closeTeaser();
      });
    }

    popup.addEventListener('click', function (e) {
      if (closeOnOutside && (e.target === popup || e.target === frame)) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) close();
    });

    /* ------------------------------------------------------------ triggers */

    if (!inTestMode && !isDesignMode() && teaserMode !== 'trigger') {
      if (triggerPages) {
        var viewKey = storageKey + '-pageviews';
        var views = int(sessionStorage.getItem(viewKey), 0) + 1;
        sessionStorage.setItem(viewKey, String(views));

        if (views >= pageCount) {
          setTimeout(function () {
            if (triggerDelay) setTimeout(function () { open('pages'); }, delayMs);
            else open('pages');
          }, 100);
        }
      } else if (triggerDelay) {
        setTimeout(function () { open('delay'); }, delayMs);
      }

      if (triggerExit) {
        var exitFired = false;
        var onExit = function (e) {
          if (exitFired || window.innerWidth < DESKTOP_MIN) return;
          if (e.relatedTarget || e.toElement) return;
          if (e.clientY > 12) return;
          exitFired = true;
          open('exit');
        };
        document.addEventListener('mouseout', onExit);
        document.documentElement.addEventListener('mouseleave', onExit);
      }

      if (triggerScroll) {
        var scrollFired = false;
        window.addEventListener(
          'scroll',
          function () {
            if (scrollFired) return;
            var max = document.documentElement.scrollHeight - window.innerHeight;
            if (max <= 0) return;
            if ((window.scrollY / max) * 100 < scrollPercent) return;
            scrollFired = true;
            open('scroll');
          },
          { passive: true }
        );
      }
    }

    // A beat after load so the entrance has something to animate from. In "after" mode this
    // only lands once the popup has been closed on an earlier page.
    if (hasTeaser) setTimeout(showTeaser, 400);

    if (teaserPreview) {
      document.addEventListener('shopify:section:select', function (event) {
        if (event.detail && event.detail.sectionId === sectionId) showTeaser();
      });

      // Selecting a page block still opens the popup on that page; closing it brings the
      // teaser back.
      document.addEventListener('shopify:block:select', function (event) {
        var page = editorSelectedPage(event);
        if (page) showTestPage(page);
      });
    }

    if (inTestMode) {
      // `animated` waits a beat so the entrance transition has something to
      // animate from; `static` snaps straight to the open state.
      if (testMode === 'animated') setTimeout(function () { showTestPage(); }, 500);
      else showTestPage();

      document.addEventListener('shopify:block:select', function (event) {
        var page = editorSelectedPage(event);
        if (page) showTestPage(page);
      });

      document.addEventListener('shopify:section:select', function (event) {
        if (event.detail && event.detail.sectionId === sectionId) showTestPage();
      });
    }

    /* -------------------------------------------------------- form submit */

    // Prep runs in the capture phase so it lands before Shopify's own submit
    // listeners (form persister, CAPTCHA). We set return_to, merge the tags
    // every child block contributed, and validate the address. The form then
    // posts natively: the CAPTCHA flow needs a real navigation, so the browser
    // comes back here with `customer_posted=true` and we restore the success
    // state below.
    function prepareForm(e) {
      if (!form) return false;

      var email = form.querySelector('input[type="email"][name="contact[email]"]');
      if (!email || !email.value || !email.checkValidity()) {
        if (e) e.preventDefault();
        if (email) email.reportValidity();
        return false;
      }

      var returnTo = form.querySelector('[data-email-popup-return-to]');
      if (returnTo) {
        var url = new URL(window.location.href);
        url.searchParams.set('customer_posted', 'true');
        url.searchParams.set('email_popup', sectionId);
        returnTo.value = url.pathname + url.search + url.hash;
      }

      var tagsInput = form.querySelector('[data-email-popup-tags]');
      if (tagsInput) {
        var base = 'newsletter, popup';
        var extra = [];

        var collect = function (raw) {
          (raw || '')
            .split(',')
            .map(function (t) { return t.trim(); })
            .forEach(function (t) {
              if (t && t !== 'newsletter' && t !== 'popup' && extra.indexOf(t) === -1) extra.push(t);
            });
        };

        // Each choice carries its own hidden contact[tags]. Left enabled they
        // would post alongside the merged value and the last one would win, so
        // they are disabled for serialization but stay readable to their block.
        popup.querySelectorAll('[data-multiple-choice] [data-tags-input]').forEach(function (input) {
          collect(input.value);
          input.disabled = true;
        });

        popup.querySelectorAll('[data-extra-tags]').forEach(function (el) {
          collect(el.getAttribute('data-extra-tags'));
        });

        tagsInput.value = extra.length ? base + ', ' + extra.join(', ') : base;
      }

      return true;
    }

    if (form) form.addEventListener('submit', prepareForm, true);

    // Child blocks with their own submit affordance route through the real
    // form so the CAPTCHA hook still fires. Shopify only arms a form's CAPTCHA
    // once a shopper focuses inside it, and these blocks' fields sit outside
    // the form, so it is armed here first; an unarmed form's submit is
    // silently stopped by Shopify.
    function submitEmail(email) {
      if (!form) return { success: false, error: 'Form not found' };

      var field = form.querySelector('input[type="email"][name="contact[email]"]');
      if (field && email) field.value = email;

      var send = function () {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else if (form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))) form.submit();
      };

      try {
        var captcha = window.Shopify && window.Shopify.captcha;
        var armed = form.hasAttribute('data-hcaptcha-bound') || form.hasAttribute('data-recaptcha-bound');
        if (captcha && typeof captcha.protect === 'function' && !armed) captcha.protect(form, send);
        else send();
      } catch (e) {
        return { success: false, error: 'Submission failed' };
      }
      return { success: false, pending: true };
    }

    /* ------------------------------------------------ restore after submit */

    var submissionParams = new URLSearchParams(window.location.search);
    // prepareForm writes this popup's section id into return_to, so a post from
    // any other customer form (a footer newsletter) never opens the success screen.
    var submissionMatches = submissionParams.get('email_popup') === sectionId;
    var justSubmitted = submissionMatches && (
      !!popup.querySelector('[data-email-popup-just-submitted]') ||
      submissionParams.get('customer_posted') === 'true');

    if (justSubmitted) {
      popup.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      showSuccess();
      popupShown = true;

      var convKey = '_sugar_popup_conv_' + sectionId;
      var counted = false;
      try {
        if (localStorage.getItem(convKey)) counted = true;
        else localStorage.setItem(convKey, '1');
      } catch (e) {}
      if (!counted) emit('convert', 'subscribe');
    }

    sessionStorage.removeItem(storageKey + '-pending');

    /* --------------------------------------------------------------- API */

    popup.emailPopupClose = close;
    popup.emailPopupShowOptIn = showOptIn;
    popup.emailPopupShowSuccess = showSuccess;
    popup.emailPopupSubmit = submitEmail;

    // A link to #email-popup opens it the way a click on the teaser does (handler below init).
    popup.emailPopupOpen = function () {
      if (isDesignMode()) showTestPage();
      else open('link', true);
    };

    // Older child blocks reach for these globally. Point them at whichever
    // popup is currently open so two popups on one page can't cross wires.
    var delegate = function (method) {
      return function () {
        var open = document.querySelector('.sugar-email-popup.is-open') || popup;
        return open[method] ? open[method].apply(open, arguments) : undefined;
      };
    };

    window.emailPopupShowOptIn = delegate('emailPopupShowOptIn');
    window.emailPopupShowSuccess = delegate('emailPopupShowSuccess');
    window.emailPopupSubmit = delegate('emailPopupSubmit');
  }

  function init() {
    document.querySelectorAll('.sugar-email-popup').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  // Any link to #email-popup (a Link Button set to Section / Block "email-popup", a Mini Link
  // Button, a link in rich text) opens the page's popup instead of jumping. Each popup section
  // loads this file, so the listener is added once.
  if (!window.__sugarEmailPopupLinks) {
    window.__sugarEmailPopupLinks = true;
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a[href="#email-popup"]');
      if (!link) return;
      var popup = document.querySelector('.sugar-email-popup');
      if (!popup || !popup.emailPopupOpen) return;
      e.preventDefault();
      popup.emailPopupOpen();
    });
  }
})();
